import { useState, useCallback, useRef, useEffect } from 'react';
import './useResizable.css';

/**
 * Custom hook для изменения размера элемента
 * Поддерживает 8 resize handles: 4 угла + 4 стороны
 *
 * Примечание: Для позиционирования handles используется CSS transform вместо
 * Tailwind translate утилит из-за проблемы с @property в Shadow DOM.
 * См. https://github.com/tailwindlabs/tailwindcss/issues/17104
 *
 * @param {Object} options - Опции для resizing
 * @param {Object} options.initialSize - Начальный размер { width, height }
 * @param {Object} options.minSize - Минимальный размер { width, height }
 * @param {Object} options.maxSize - Максимальный размер { width, height }
 * @param {Function} options.onResizeEnd - Callback при завершении resize
 * @param {boolean} options.disabled - Отключить resizing
 * @returns {Object} { size, resizeHandles, isResizing, resetSize }
 */
export const useResizable = ({
  initialSize = { width: 450, height: 600 },
  minSize = { width: 350, height: 400 },
  maxSize = { width: 800, height: 900 },
  onResizeEnd,
  disabled = false
} = {}) => {
  const [size, setSize] = useState(initialSize);
  const [isResizing, setIsResizing] = useState(false);
  const [activeHandle, setActiveHandle] = useState(null);

  const resizeStartRef = useRef({ x: 0, y: 0 });
  const elementSizeRef = useRef({ width: 0, height: 0 });

  // Update size when initialSize changes (e.g., from localStorage)
  useEffect(() => {
    setSize(initialSize);
  }, [initialSize.width, initialSize.height]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !activeHandle) return;

    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;

    let newWidth = elementSizeRef.current.width;
    let newHeight = elementSizeRef.current.height;

    // Apply deltas based on active handle
    switch (activeHandle) {
      // Corners
      case 'nw': // top-left
        newWidth = elementSizeRef.current.width - deltaX;
        newHeight = elementSizeRef.current.height - deltaY;
        break;
      case 'ne': // top-right
        newWidth = elementSizeRef.current.width + deltaX;
        newHeight = elementSizeRef.current.height - deltaY;
        break;
      case 'sw': // bottom-left
        newWidth = elementSizeRef.current.width - deltaX;
        newHeight = elementSizeRef.current.height + deltaY;
        break;
      case 'se': // bottom-right
        newWidth = elementSizeRef.current.width + deltaX;
        newHeight = elementSizeRef.current.height + deltaY;
        break;

      // Sides
      case 'n': // top
        newHeight = elementSizeRef.current.height - deltaY;
        break;
      case 's': // bottom
        newHeight = elementSizeRef.current.height + deltaY;
        break;
      case 'w': // left
        newWidth = elementSizeRef.current.width - deltaX;
        break;
      case 'e': // right
        newWidth = elementSizeRef.current.width + deltaX;
        break;

      default:
        break;
    }

    // Apply min/max constraints
    newWidth = Math.max(minSize.width, Math.min(newWidth, maxSize.width));
    newHeight = Math.max(minSize.height, Math.min(newHeight, maxSize.height));

    setSize({ width: newWidth, height: newHeight });
  }, [isResizing, activeHandle, minSize, maxSize]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      setActiveHandle(null);

      if (onResizeEnd) {
        onResizeEnd(size);
      }
    }
  }, [isResizing, size, onResizeEnd]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Prevent text selection during resize
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const createHandleMouseDown = useCallback((handlePosition) => (e) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation(); // Prevent triggering parent's drag

    setIsResizing(true);
    setActiveHandle(handlePosition);
    resizeStartRef.current = { x: e.clientX, y: e.clientY };
    elementSizeRef.current = { ...size };
  }, [size, disabled]);

  const resetSize = useCallback(() => {
    setSize(initialSize);
  }, [initialSize]);

  // Generate resize handles with their positions and cursors
  // Note: Using CSS classes instead of Tailwind translate-* utilities for Shadow DOM compatibility
  const resizeHandles = [
    { position: 'nw', cursor: 'nwse-resize', className: 'top-0 left-0 resize-handle-nw' },
    { position: 'n', cursor: 'ns-resize', className: 'top-0 left-1/2 resize-handle-n' },
    { position: 'ne', cursor: 'nesw-resize', className: 'top-0 right-0 resize-handle-ne' },
    { position: 'e', cursor: 'ew-resize', className: 'top-1/2 right-0 resize-handle-e' },
    { position: 'se', cursor: 'nwse-resize', className: 'bottom-0 right-0 resize-handle-se' },
    { position: 's', cursor: 'ns-resize', className: 'bottom-0 left-1/2 resize-handle-s' },
    { position: 'sw', cursor: 'nesw-resize', className: 'bottom-0 left-0 resize-handle-sw' },
    { position: 'w', cursor: 'ew-resize', className: 'top-1/2 left-0 resize-handle-w' },
  ].map(handle => ({
    ...handle,
    onMouseDown: createHandleMouseDown(handle.position),
  }));

  return {
    size,
    resizeHandles,
    isResizing,
    activeHandle,
    resetSize,
    setSize, // Allow external size updates
  };
};
